import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listDomainConfigChanges,
  resolveDomainConfig,
  updateDomainConfig
} from './domain-config-api';
import { DOMAIN_CONFIG_QUERY_KEY, UpdateDomainConfigInput } from './domain-config-constants';

/**
 * Resolves the canonical domain-config row for a URL, if one exists. `data`
 * is `null` for a domain this tool has never seen before - the common case
 * for a brand-new domain, not an error (see domain-config-api.ts).
 */
export const useDomainConfigResolve = (url: string) =>
  useQuery({
    queryKey: [DOMAIN_CONFIG_QUERY_KEY, 'resolve', url],
    queryFn: () => resolveDomainConfig(url),
    enabled: Boolean(url)
  });

export const useDomainConfigChanges = (domain: string | undefined) =>
  useQuery({
    queryKey: [DOMAIN_CONFIG_QUERY_KEY, 'changes', domain],
    queryFn: () => listDomainConfigChanges(domain ?? ''),
    enabled: Boolean(domain)
  });

/**
 * Refetches every domain-config query (resolve + change log). Use after any
 * write - including the scan-time create - so a cached "no config yet" 404
 * for a just-created domain doesn't linger for the query's staleTime.
 */
export const useInvalidateDomainConfig = () => {
  const queryClient = useQueryClient();
  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: [DOMAIN_CONFIG_QUERY_KEY] }),
    [queryClient]
  );
};

/**
 * Audited client_type/vertical edit. Invalidates on success and on failure
 * (a 409 means someone else's values need to be picked up).
 */
export const useUpdateDomainConfig = (domain: string) => {
  const invalidate = useInvalidateDomainConfig();
  return useMutation({
    mutationFn: (input: UpdateDomainConfigInput) => updateDomainConfig(domain, input),
    onSettled: invalidate
  });
};
